<template>
  <nav role="navigation" class="owc-vertical-navigation">
    <ol :class="['owc-vertical-navigation__list', animationClass]">
      <li
        v-for="(anchorItem, index) in anchorItems"
        :key="anchorItem.id"
        :class="[
          'owc-vertical-navigation__item',
          { 'owc-vertical-navigation__item--active': activeItemIndex === index },
        ]"
      >
        <a :href="`#${anchorItem.id}`" class="owc-vertical-navigation__anchor">
          <div class="owc-vertical-navigation__icon-wrapper">
            <div class="owc-vertical-navigation__icon"></div>
          </div>
          <span
            v-if="activeItemIndex === index && sectionClass"
            class="owc-vertical-navigation__text-indicator"
          ></span>
          <span
            :class="[
              'owc-vertical-navigation__text',
              'wb-type-button-secondary',
              {
                [sectionClass]: activeItemIndex === index,
              },
            ]"
          >
            {{ anchorItem.label }}
          </span>
        </a>
      </li>
    </ol>
    <div
      v-if="scrollHint"
      :class="[
        'owc-vertical-navigation__arrow-wrapper',
        { 'owc-vertical-navigation__arrow-wrapper--hidden': !isScrollHintVisible },
      ]"
    >
      <wb-icon
        class="owc-vertical-navigation__arrow-hint"
        aria-hidden="true"
        :name="`arrow-${scrollHint.direction}`"
      ></wb-icon>
      <span class="owc-vertical-navigation__bottom-text wb-type-hint">{{ scrollHint.label }}</span>
    </div>
  </nav>
</template>

<script lang="ts">
/**
 * `VerticalNavigation` - Component used to to help the user navigate through different sections of the PI page.
 * At the moment this component is only being displayed on MQ5 or larger screens.
 */
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import { VerticalNavigationComponent } from './VerticalNavigation';
import {
  ANCHOR_ATTR_NAME,
  COMPONENT_NAME,
  INITIAL_SHOW_TIME,
  SECTION_SHOW_TIME,
} from './VerticalNavigation.config';

export default (ComponentInitializeDispatcher as VerticalNavigationComponent).extend({
  name: COMPONENT_NAME,
  data: () => ({
    activeItemIndex: -1,
    animationClass: '',
    sectionClass: '',
    timeout: undefined,
    observer: undefined,
    sectionsDataAnchorIds: [],
    sections: [],
  }),
  mounted() {
    this.setupIntersectionObserver();
    this.observeSections(`[${ANCHOR_ATTR_NAME}]`);
    this.listenForNewSections();
    this.activeItemIndex = 0;
    this.animationClass = 'owc-vertical-navigation__list--open';
    window.setTimeout(() => {
      this.animationClass = '';
    }, INITIAL_SHOW_TIME);
  },
  beforeDestroy() {
    this.handleDestroy();
  },
  computed: {
    anchorItems: function() {
      return this.componentData?.anchorItems;
    },
    scrollHint: function() {
      return this.componentData?.scrollHint;
    },
    isScrollHintVisible: function() {
      return this.activeItemIndex <= 0;
    },
  },
  methods: {
    setupIntersectionObserver: function(): void {
      const options = {
        rootMargin: '0px',
        threshold: 0.5,
      };
      this.observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute(ANCHOR_ATTR_NAME);
            const currentIndex = (this.anchorItems || []).findIndex(anchorItem => anchorItem.id === id);
            if (currentIndex > -1) {
              this.activeItemIndex = currentIndex;
            }
          }
        });
      }, options);
    },
    observeSections: function(query): void {
      const parentElement = (this.$el.getRootNode() as ShadowRoot).host?.parentElement;

      if (!parentElement) {
        return;
      }

      const sections = [...parentElement.querySelectorAll(query)];

      if (!sections.length) {
        return;
      }

      if (!this.areNewSections(sections)) {
        return;
      }

      this.sections.forEach(section => this.observer?.observe(section));
    },
    eventListener: function(event: CustomEvent<{ section: string }>) {
      this.observeSections(`[${ANCHOR_ATTR_NAME}="${event.detail.section}"]`);
    },
    listenForNewSections: function() {
      window.addEventListener('owc-vertical-navigation-add-section', this.eventListener as EventListener);
    },
    areNewSections: function(sections) {
      const newSectionsDataAnchorIds = sections.map(
        section => section.getAttribute('data-anchor-id') as string,
      );
      const combinedSectionsDataAnchorIds = [
        ...new Set([...this.sectionsDataAnchorIds, ...newSectionsDataAnchorIds]),
      ];
      if (combinedSectionsDataAnchorIds.length === this.sectionsDataAnchorIds.length) {
        return false;
      }
      this.sectionsDataAnchorIds = combinedSectionsDataAnchorIds;
      this.sections = [...new Set([...this.sections, ...sections])];
      return true;
    },
    handleDestroy: function() {
      window.removeEventListener('owc-vertical-navigation-add-section', this.eventListener as EventListener);
      this.sections.forEach((section: Element) => {
        this.observer?.unobserve(section);
      });
      this.observer?.disconnect();
    },
  },
  watch: {
    activeItemIndex: function() {
      this.sectionClass = 'owc-vertical-navigation__show';
      clearTimeout(this.timeout);
      this.timeout = window.setTimeout(() => {
        this.sectionClass = '';
      }, SECTION_SHOW_TIME);
    },
  },
});
</script>

<style lang="scss">
@import '@owc-styles/utils/z-index-list';
@import '@owc-styles/utils/rtl-utils';

$module-name: 'owc-vertical-navigation';
$icon-size: 4px;

.#{$module-name} {
  @include breakpoint-between(mq1, mq5) {
    display: none;
  }

  position: fixed;
  top: 50%;
  left: 0;
  z-index: $z-index-content-lvl1;

  transform: translateY(-50%);

  &__list {
    padding: var(--wb-spacing-xxs) var(--wb-spacing-xxs) var(--wb-spacing-xxs) 40px;

    transition: background-color 300ms;

    &:hover,
    &--open {
      background-color: var(--wb-white);
      box-shadow: var(--wb-shadow-level-1);

      .owc-vertical-navigation__text-indicator {
        display: none;
      }
      .#{$module-name}__item--active .#{$module-name}__icon {
        background-color: var(--wb-black);
      }
      .#{$module-name}__text {
        display: inline-block;
      }
      .#{$module-name}__show {
        background-color: unset;
        box-shadow: unset;
      }

      + .#{$module-name}__arrow-wrapper {
        visibility: hidden;
      }
    }
  }
  &__item {
    display: flex;
    align-items: center;

    &--active {
      .#{$module-name}__text {
        color: var(--wb-grey-20);
      }

      .#{$module-name}__icon {
        width: $icon-size * 2;
        height: $icon-size * 2;
      }
    }
    &:last-child {
      margin-bottom: 0;
    }
  }
  &__anchor {
    display: flex;
    align-items: center;
    justify-content: center;

    color: var(--wb-grey-55);

    text-decoration: none;

    &:active,
    &:hover {
      .#{$module-name}__icon {
        background-color: var(--wb-black);
      }

      .#{$module-name}__text {
        color: var(--wb-grey-20);
      }
    }
    & > * {
      pointer-events: none;
    }
  }
  &__icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
  }
  &__icon {
    display: flex;

    width: $icon-size;
    height: $icon-size;

    border-radius: 100%;

    vertical-align: middle;

    background-color: var(--wb-grey-55);
  }
  &__text {
    display: none;
    padding: 4px 8px;

    border-radius: 5px;

    vertical-align: middle;
  }
  &__text-indicator {
    border-color: transparent var(--wb-white) transparent transparent;
    border-style: solid;
    border-width: 6px;

    filter: drop-shadow(0px 0px 10px var(--wb-white));
  }
  &__show {
    display: inline-block;

    background-color: var(--wb-white);
    box-shadow: rgba($wb-black, 0.25) -2px 0px 10px 1px;
  }
  &__arrow-wrapper {
    width: 70px;
    margin-left: 19px;

    text-align: center;

    transition: transform 1s;

    &--hidden {
      visibility: hidden;
    }
  }
  &__arrow-hint {
    width: 12px;
    height: 12px;

    color: var(--wb-grey-55);

    &[role='button'] {
      cursor: pointer;
    }
    & > svg {
      @keyframes bounce {
        0%,
        100% {
          transform: translateY(-25%);

          animation-timing-function: cubic-bezier(0.8, 0, 1, 1);
        }
        50% {
          transform: none;

          animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
        }
      }
      animation: bounce 1s infinite;

      @media (prefers-reduced-motion) {
        & {
          animation: none;
        }
      }
    }
  }
  &__bottom-text {
    display: block;
    margin-top: 6px;

    color: var(--wb-grey-55);
  }
}

@include rtl-rule('.owc-vertical-navigation') {
  right: 0;
  left: unset;

  .owc-vertical-navigation__list {
    padding-right: 40px;
    padding-left: var(--wb-spacing-xxs);
  }
  .owc-vertical-navigation__arrow-wrapper {
    margin-right: 19px;
    margin-left: unset;
  }
}
</style>
